/**
 * Format helpers used across pages. Locale 'ru-RU' for numbers, explicit
 * rounding rules so cost doesn't get either scientific or 12-decimal noise.
 */

export function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("ru-RU");
}

/** Rubles — chooses precision based on magnitude. */
export function fmtRub(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1) return n.toFixed(4) + " ₽";
  if (n < 1000) return n.toFixed(2) + " ₽";
  return Math.round(n).toLocaleString("ru-RU") + " ₽";
}

/** US dollars, with extra precision for sub-cent embedding costs. */
export function fmtUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 0.01) return "$" + n.toFixed(6);
  if (n < 10) return "$" + n.toFixed(4);
  return "$" + n.toFixed(2);
}

/** Bounded purpose → tailwind class mapping for badges/charts. Stable across
 * the app so a "chat" bar in a chart matches the "chat" pill in a table. */
export const PURPOSE_COLOURS: Record<string, { dot: string; bg: string; text: string }> = {
  chat:          { dot: "#2f6feb", bg: "bg-[#2f6feb]/10", text: "text-[#2f6feb] dark:text-[#7ca5ff]" },
  vision:        { dot: "#8b5cf6", bg: "bg-[#8b5cf6]/10", text: "text-[#8b5cf6] dark:text-[#b591ff]" },
  audio:         { dot: "#06b6d4", bg: "bg-[#06b6d4]/10", text: "text-[#06b6d4] dark:text-[#5fd6f0]" },
  assistant_speech: { dot: "#7c3aed", bg: "bg-[#7c3aed]/10", text: "text-[#6d28d9] dark:text-[#c4b5fd]" },
  voice_transcription: { dot: "#0891b2", bg: "bg-[#0891b2]/10", text: "text-[#0e7490] dark:text-[#67e8f9]" },
  search:        { dot: "#f59e0b", bg: "bg-[#f59e0b]/10", text: "text-[#b45309] dark:text-[#fbbf24]" },
  extraction:    { dot: "#16a34a", bg: "bg-[#16a34a]/10", text: "text-[#15803d] dark:text-[#4ade80]" },
  consolidation: { dot: "#84cc16", bg: "bg-[#84cc16]/10", text: "text-[#4d7c0f] dark:text-[#bef264]" },
  proactive:      { dot: "#ec4899", bg: "bg-[#ec4899]/10", text: "text-[#be185d] dark:text-[#f9a8d4]" },
  embedding:     { dot: "#64748b", bg: "bg-muted", text: "text-muted-foreground" },
  unknown:       { dot: "#6b7280", bg: "bg-muted", text: "text-muted-foreground" },
};

export function purposeColour(p: string) {
  return PURPOSE_COLOURS[p] ?? PURPOSE_COLOURS.unknown;
}

export const PURPOSE_LABELS: Record<string, string> = {
  chat: "Ответ персонажа",
  vision: "Ответ по изображению",
  audio: "Ответ на голосовое сообщение",
  search: "Поиск в интернете",
  extraction: "Запоминание важных деталей",
  consolidation: "Обновление памяти",
  embedding: "Подготовка памяти к поиску",
  proactive: "Инициатива персонажа",
  assistant_speech: "Озвучка ответа",
  voice_transcription: "Распознавание голоса",
  image_generation: "Генерация изображений",
  image_edit: "Редактирование изображений",
};

export function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? "Другая операция";
}

const PURPOSE_DESCRIPTIONS: Record<string, string> = {
  chat: "Персонаж читает ваше сообщение, нужную историю и память, затем составляет ответ.",
  vision: "Персонаж рассматривает присланное изображение и отвечает на ваш вопрос.",
  audio: "Персонаж обрабатывает голосовое сообщение и готовит ответ.",
  search: "Поиск свежей информации и источников для ответа.",
  extraction: "Выделение важных фактов из разговора, чтобы не спрашивать одно и то же снова.",
  consolidation: "Упорядочивание и обновление уже сохранённых воспоминаний.",
  embedding: "Преобразование текста для поиска нужных воспоминаний по смыслу.",
  proactive: "Подготовка сообщения, с которым персонаж обращается к вам сам.",
  assistant_speech: "Превращение текста ответа в голос.",
  voice_transcription: "Превращение вашей голосовой записи в текст.",
  image_generation: "Создание картинки по вашему запросу.",
  image_edit: "Изменение присланной картинки по вашему запросу.",
};
export const purposeDescription = (purpose: string) =>
  PURPOSE_DESCRIPTIONS[purpose] ?? "Вызов ИИ для работы сервиса. Подробности можно уточнить в поддержке.";
